import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from 'react';

// Conservative defaults when LD is loading or fails to initialize.
// Only core features: Composer, Data Collections, Guardrails, Release Notes, Evaluation.
const SAFE_DEFAULTS = {
  isMaintenanceMode: false,
  maintenanceMessage: '',
  isEvalsEnabled: true,
  isScheduleEnabled: false,
  isWorkflowEnabled: true,
  isTxt2sqlEnabled: false,
  isDataSourcesEnabled: true,
  isContextGraphsEnabled: false,
  isSourcesEnabled: false,
  isUnstructuredCollectionsEnabled: true,
  isStructuredCollectionsEnabled: false,
  isCollectionSourceEnabled: false,
  isDocumentationEnabled: false,
  isApiKeysEnabled: false,
  isUserApiTokensEnabled: false,
  isDocsWorkflowApiEnabled: false,
  isReleaseNotesEnabled: true,
  sandboxBannerMessage: '',
  isCollectionChatEnabled: false,
  isChatAgentsEnabled: false,
  isAgentsEnabled: false,
  isPrimaryChatEnabled: false,
  isChatsPageEnabled: false,
  isChatSharingEnabled: false,
  isComposerDashboardEnabled: false,
  homeRedirectRoute: '/workflow',
  degradedServiceBannerMessage: '',
  scheduledMaintenanceBannerMessage: '',
  isGuardrailActiveToggleEnabled: false,
  isOrgNodesToolsEnabled: false,
  isOrgMcpToolsEnabled: false,
  isTriggersEnabled: false,
  isFeedbackEnabled: false,
  isLabelsEnabled: false,
  isDashboardUserInfoEnabled: false,
  isDashboardModelDistributionEnabled: true,
  isDashboardTokenSpendEnabled: false,
  isDashboardTopWorkflowsCostEnabled: false,
  isBuilderAgentOwnerOnly: false,
  isShareClientDataWarningEnabled: false,
  isExternalChatEnabled: false,
  isUploadApiEnabled: false,
  isWorkflowLockingEnabled: false,
  isQualificationEnabled: false,
  isWorkflowSchedulingEnabled: false,
  isWorkflowRunCostEnabled: false,
  isScheduleRunCostEnabled: false,
  isSkillsEnabled: false,
  isArtifactsEnabled: false,
  isFilesEnabled: false,
  isCanvasA2aChatEnabled: false,
  isCreatorEmailTruncated: false,
  isNotificationCenterEnabled: false,
  isEvalCriteriaAutogenEnabled: false,
  isGuardrailsConfigAutogenEnabled: false,
};

interface LaunchDarklyContextType {
  isMaintenanceMode: boolean;
  maintenanceMessage: string;
  isEvalsEnabled: boolean;
  isScheduleEnabled: boolean;
  isWorkflowEnabled: boolean;
  isTxt2sqlEnabled: boolean;
  isDataSourcesEnabled: boolean;
  isContextGraphsEnabled: boolean;
  isSourcesEnabled: boolean;
  isUnstructuredCollectionsEnabled: boolean;
  isStructuredCollectionsEnabled: boolean;
  isCollectionSourceEnabled: boolean;
  isDocumentationEnabled: boolean;
  isApiKeysEnabled: boolean;
  isUserApiTokensEnabled: boolean;
  isDocsWorkflowApiEnabled: boolean;
  isReleaseNotesEnabled: boolean;
  sandboxBannerMessage: string;
  isCollectionChatEnabled: boolean;
  isChatAgentsEnabled: boolean;
  isAgentsEnabled: boolean;
  isPrimaryChatEnabled: boolean;
  isChatsPageEnabled: boolean;
  isChatSharingEnabled: boolean;
  isComposerDashboardEnabled: boolean;
  homeRedirectRoute: string;
  degradedServiceBannerMessage: string;
  scheduledMaintenanceBannerMessage: string;
  isGuardrailActiveToggleEnabled: boolean;
  isOrgNodesToolsEnabled: boolean;
  isOrgMcpToolsEnabled: boolean;
  isTriggersEnabled: boolean;
  isFeedbackEnabled: boolean;
  isLabelsEnabled: boolean;
  isDashboardUserInfoEnabled: boolean;
  isDashboardModelDistributionEnabled: boolean;
  isDashboardTokenSpendEnabled: boolean;
  isDashboardTopWorkflowsCostEnabled: boolean;
  isBuilderAgentOwnerOnly: boolean;
  isShareClientDataWarningEnabled: boolean;
  isExternalChatEnabled: boolean;
  isUploadApiEnabled: boolean;
  isWorkflowLockingEnabled: boolean;
  isQualificationEnabled: boolean;
  isWorkflowSchedulingEnabled: boolean;
  isWorkflowRunCostEnabled: boolean;
  isScheduleRunCostEnabled: boolean;
  isSkillsEnabled: boolean;
  isArtifactsEnabled: boolean;
  isFilesEnabled: boolean;
  isCanvasA2aChatEnabled: boolean;
  isCreatorEmailTruncated: boolean;
  isNotificationCenterEnabled: boolean;
  isEvalCriteriaAutogenEnabled: boolean;
  isGuardrailsConfigAutogenEnabled: boolean;
  ldClient: unknown;
  isLoading: boolean;
}

const LaunchDarklyContext = createContext<LaunchDarklyContextType | undefined>(
  undefined,
);

export const useLaunchDarkly = () => {
  const context = useContext(LaunchDarklyContext);
  if (context === undefined) {
    throw new Error(
      'useLaunchDarkly must be used within a LaunchDarklyProvider',
    );
  }
  return context;
};

interface LaunchDarklyProviderProps {
  children: ReactNode;
  orgId?: string;
}

export function LaunchDarklyProvider({
  children,
  orgId,
}: LaunchDarklyProviderProps) {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(
    SAFE_DEFAULTS.isMaintenanceMode,
  );
  const [maintenanceMessage, setMaintenanceMessage] = useState(
    SAFE_DEFAULTS.maintenanceMessage,
  );
  const [isEvalsEnabled, setIsEvalsEnabled] = useState(
    SAFE_DEFAULTS.isEvalsEnabled,
  );
  const [isScheduleEnabled, setIsScheduleEnabled] = useState(
    SAFE_DEFAULTS.isScheduleEnabled,
  );
  const [isWorkflowEnabled, setIsWorkflowEnabled] = useState(
    SAFE_DEFAULTS.isWorkflowEnabled,
  );
  const [isTxt2sqlEnabled, setIsTxt2sqlEnabled] = useState(
    SAFE_DEFAULTS.isTxt2sqlEnabled,
  );
  const [isDataSourcesEnabled, setIsDataSourcesEnabled] = useState(
    SAFE_DEFAULTS.isDataSourcesEnabled,
  );
  const [isContextGraphsEnabled, setIsContextGraphsEnabled] = useState(
    SAFE_DEFAULTS.isContextGraphsEnabled,
  );
  const [isSourcesEnabled, setIsSourcesEnabled] = useState(
    SAFE_DEFAULTS.isSourcesEnabled,
  );
  const [
    isUnstructuredCollectionsEnabled,
    setIsUnstructuredCollectionsEnabled,
  ] = useState(SAFE_DEFAULTS.isUnstructuredCollectionsEnabled);
  const [isStructuredCollectionsEnabled, setIsStructuredCollectionsEnabled] =
    useState(SAFE_DEFAULTS.isStructuredCollectionsEnabled);
  const [isCollectionSourceEnabled, setIsCollectionSourceEnabled] = useState(
    SAFE_DEFAULTS.isCollectionSourceEnabled,
  );
  const [isDocumentationEnabled, setIsDocumentationEnabled] = useState(
    SAFE_DEFAULTS.isDocumentationEnabled,
  );
  const [isApiKeysEnabled, setIsApiKeysEnabled] = useState(
    SAFE_DEFAULTS.isApiKeysEnabled,
  );
  const [isUserApiTokensEnabled, setIsUserApiTokensEnabled] = useState(
    SAFE_DEFAULTS.isUserApiTokensEnabled,
  );
  const [isDocsWorkflowApiEnabled, setIsDocsWorkflowApiEnabled] = useState(
    SAFE_DEFAULTS.isDocsWorkflowApiEnabled,
  );
  const [isReleaseNotesEnabled, setIsReleaseNotesEnabled] = useState(
    SAFE_DEFAULTS.isReleaseNotesEnabled,
  );
  const [sandboxBannerMessage, setSandboxBannerMessage] = useState(
    SAFE_DEFAULTS.sandboxBannerMessage,
  );
  const [isCollectionChatEnabled, setIsCollectionChatEnabled] = useState(
    SAFE_DEFAULTS.isCollectionChatEnabled,
  );
  const [isChatAgentsEnabled, setIsChatAgentsEnabled] = useState(
    SAFE_DEFAULTS.isChatAgentsEnabled,
  );
  const [isAgentsEnabled, setIsAgentsEnabled] = useState(
    SAFE_DEFAULTS.isAgentsEnabled,
  );
  const [isPrimaryChatEnabled, setIsPrimaryChatEnabled] = useState(
    SAFE_DEFAULTS.isPrimaryChatEnabled,
  );
  const [isChatsPageEnabled, setIsChatsPageEnabled] = useState(
    SAFE_DEFAULTS.isChatsPageEnabled,
  );
  const [isChatSharingEnabled, setIsChatSharingEnabled] = useState(
    SAFE_DEFAULTS.isChatSharingEnabled,
  );
  const [isComposerDashboardEnabled, setIsComposerDashboardEnabled] = useState(
    SAFE_DEFAULTS.isComposerDashboardEnabled,
  );
  const [homeRedirectRoute, setHomeRedirectRoute] = useState(
    SAFE_DEFAULTS.homeRedirectRoute,
  );
  const [degradedServiceBannerMessage, setDegradedServiceBannerMessage] =
    useState(SAFE_DEFAULTS.degradedServiceBannerMessage);
  const [
    scheduledMaintenanceBannerMessage,
    setScheduledMaintenanceBannerMessage,
  ] = useState(SAFE_DEFAULTS.scheduledMaintenanceBannerMessage);
  const [isGuardrailActiveToggleEnabled, setIsGuardrailActiveToggleEnabled] =
    useState(SAFE_DEFAULTS.isGuardrailActiveToggleEnabled);
  const [isOrgNodesToolsEnabled, setIsOrgNodesToolsEnabled] = useState(
    SAFE_DEFAULTS.isOrgNodesToolsEnabled,
  );
  const [isOrgMcpToolsEnabled, setIsOrgMcpToolsEnabled] = useState(
    SAFE_DEFAULTS.isOrgMcpToolsEnabled,
  );
  const [isTriggersEnabled, setIsTriggersEnabled] = useState(
    SAFE_DEFAULTS.isTriggersEnabled,
  );
  const [isFeedbackEnabled, setIsFeedbackEnabled] = useState(
    SAFE_DEFAULTS.isFeedbackEnabled,
  );
  const [isLabelsEnabled, setIsLabelsEnabled] = useState(
    SAFE_DEFAULTS.isLabelsEnabled,
  );
  const [isDashboardUserInfoEnabled, setIsDashboardUserInfoEnabled] = useState(
    SAFE_DEFAULTS.isDashboardUserInfoEnabled,
  );
  const [
    isDashboardModelDistributionEnabled,
    setIsDashboardModelDistributionEnabled,
  ] = useState(SAFE_DEFAULTS.isDashboardModelDistributionEnabled);
  const [isDashboardTokenSpendEnabled, setIsDashboardTokenSpendEnabled] =
    useState(SAFE_DEFAULTS.isDashboardTokenSpendEnabled);
  const [
    isDashboardTopWorkflowsCostEnabled,
    setIsDashboardTopWorkflowsCostEnabled,
  ] = useState(SAFE_DEFAULTS.isDashboardTopWorkflowsCostEnabled);
  const [isBuilderAgentOwnerOnly, setIsBuilderAgentOwnerOnly] = useState(
    SAFE_DEFAULTS.isBuilderAgentOwnerOnly,
  );
  const [isShareClientDataWarningEnabled, setIsShareClientDataWarningEnabled] =
    useState(SAFE_DEFAULTS.isShareClientDataWarningEnabled);
  const [isExternalChatEnabled, setIsExternalChatEnabled] = useState(
    SAFE_DEFAULTS.isExternalChatEnabled,
  );
  const [isUploadApiEnabled, setIsUploadApiEnabled] = useState(
    SAFE_DEFAULTS.isUploadApiEnabled,
  );
  const [isWorkflowLockingEnabled, setIsWorkflowLockingEnabled] = useState(
    SAFE_DEFAULTS.isWorkflowLockingEnabled,
  );
  const [isQualificationEnabled, setIsQualificationEnabled] = useState(
    SAFE_DEFAULTS.isQualificationEnabled,
  );
  const [isWorkflowSchedulingEnabled, setIsWorkflowSchedulingEnabled] =
    useState(SAFE_DEFAULTS.isWorkflowSchedulingEnabled);
  const [isWorkflowRunCostEnabled, setIsWorkflowRunCostEnabled] = useState(
    SAFE_DEFAULTS.isWorkflowRunCostEnabled,
  );
  const [isScheduleRunCostEnabled, setIsScheduleRunCostEnabled] = useState(
    SAFE_DEFAULTS.isScheduleRunCostEnabled,
  );
  const [isSkillsEnabled, setIsSkillsEnabled] = useState(
    SAFE_DEFAULTS.isSkillsEnabled,
  );
  const [isArtifactsEnabled, setIsArtifactsEnabled] = useState(
    SAFE_DEFAULTS.isArtifactsEnabled,
  );
  const [isFilesEnabled, setIsFilesEnabled] = useState(
    SAFE_DEFAULTS.isFilesEnabled,
  );
  const [isCanvasA2aChatEnabled, setIsCanvasA2aChatEnabled] = useState(
    SAFE_DEFAULTS.isCanvasA2aChatEnabled,
  );
  const [isCreatorEmailTruncated, setIsCreatorEmailTruncated] = useState(
    SAFE_DEFAULTS.isCreatorEmailTruncated,
  );
  const [isNotificationCenterEnabled, setIsNotificationCenterEnabled] =
    useState(SAFE_DEFAULTS.isNotificationCenterEnabled);
  const [isEvalCriteriaAutogenEnabled, setIsEvalCriteriaAutogenEnabled] =
    useState(SAFE_DEFAULTS.isEvalCriteriaAutogenEnabled);
  const [
    isGuardrailsConfigAutogenEnabled,
    setIsGuardrailsConfigAutogenEnabled,
  ] = useState(SAFE_DEFAULTS.isGuardrailsConfigAutogenEnabled);
  const [ldClient, setLdClient] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Initialize LaunchDarkly client
    const initLaunchDarkly = async () => {
      try {
        const clientID = import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID;

        if (!clientID) {
          // No LD configured (local dev) — enable all features for development
          console.info(
            'LaunchDarkly client ID not found; running with all feature flags enabled for local development',
          );
          setIsMaintenanceMode(false);
          setIsEvalsEnabled(true);
          setIsScheduleEnabled(true);
          setIsWorkflowEnabled(true);
          setIsTxt2sqlEnabled(true);
          setIsDataSourcesEnabled(true);
          setIsContextGraphsEnabled(true);
          setIsSourcesEnabled(true);
          setIsUnstructuredCollectionsEnabled(true);
          setIsStructuredCollectionsEnabled(true);
          setIsCollectionSourceEnabled(true);
          setIsDocumentationEnabled(true);
          setIsApiKeysEnabled(true);
          setIsUserApiTokensEnabled(true);
          setIsDocsWorkflowApiEnabled(true);
          setIsReleaseNotesEnabled(true);
          setSandboxBannerMessage('');
          setIsCollectionChatEnabled(true);
          setIsChatAgentsEnabled(true);
          setIsAgentsEnabled(true);
          setIsPrimaryChatEnabled(true);
          setIsChatsPageEnabled(true);
          setIsChatSharingEnabled(true);
          setIsComposerDashboardEnabled(true);
          setHomeRedirectRoute('');
          setDegradedServiceBannerMessage('');
          setScheduledMaintenanceBannerMessage('');
          setIsGuardrailActiveToggleEnabled(true);
          setIsOrgNodesToolsEnabled(true);
          setIsOrgMcpToolsEnabled(true);
          setIsTriggersEnabled(true);
          setIsFeedbackEnabled(true);
          setIsLabelsEnabled(true);
          setIsDashboardUserInfoEnabled(false);
          setIsDashboardModelDistributionEnabled(true);
          setIsDashboardTokenSpendEnabled(true);
          setIsDashboardTopWorkflowsCostEnabled(true);
          setIsBuilderAgentOwnerOnly(false);
          setIsShareClientDataWarningEnabled(true);
          setIsExternalChatEnabled(true);
          setIsUploadApiEnabled(true);
          setIsWorkflowLockingEnabled(true);
          setIsQualificationEnabled(true);
          setIsWorkflowSchedulingEnabled(true);
          setIsWorkflowRunCostEnabled(true);
          setIsScheduleRunCostEnabled(true);
          setIsSkillsEnabled(true);
          setIsNotificationCenterEnabled(true);
          setIsArtifactsEnabled(true);
          setIsFilesEnabled(true);
          setIsCanvasA2aChatEnabled(true);
          // The two autogen flags are omitted on purpose — see the flag reads below.
          setIsLoading(false);
          return;
        }

        const LD = await import('launchdarkly-js-client-sdk');

        const userContext = orgId
          ? {
              key: orgId,
              custom: {
                orgId: orgId,
              },
            }
          : {
              key: 'anonymous-user',
              anonymous: true,
            };

        const client = LD.initialize(clientID, userContext);

        await client.waitForInitialization(5); // 5 second timeout
        setLdClient(client);

        // Get maintenance mode flag - string value enables outage with custom message
        const maintenanceFlag = client.variation('platform-fe-outage', '');
        setIsMaintenanceMode(!!maintenanceFlag);
        setMaintenanceMessage(maintenanceFlag || '');

        // Get evals enabled flag
        const evalsEnabledFlag = client.variation('platform-fe-evals', true);
        setIsEvalsEnabled(evalsEnabledFlag);

        // Get schedule enabled flag
        const scheduleEnabledFlag = client.variation(
          'platform-fe-schedule',
          true,
        );
        setIsScheduleEnabled(scheduleEnabledFlag);

        // Get workflow enabled flag
        const workflowEnabledFlag = client.variation(
          'platform-fe-workflow',
          true,
        );
        setIsWorkflowEnabled(workflowEnabledFlag);

        // Get txt2sql enabled flag
        const txt2sqlEnabledFlag = client.variation(
          'platform-fe-txt2sql',
          true,
        );
        setIsTxt2sqlEnabled(txt2sqlEnabledFlag);

        // Get data sources enabled flag
        const dataSourcesEnabledFlag = client.variation(
          'platform-fe-data-sources',
          true,
        );
        setIsDataSourcesEnabled(dataSourcesEnabledFlag);

        // Get context graphs enabled flag
        const contextGraphsEnabledFlag = client.variation(
          'platform-fe-context-graphs',
          SAFE_DEFAULTS.isContextGraphsEnabled,
        );
        setIsContextGraphsEnabled(contextGraphsEnabledFlag);
        const sourcesEnabledFlag = client.variation(
          'platform-fe-sources',
          false,
        );
        setIsSourcesEnabled(sourcesEnabledFlag);

        // Get unstructured collections enabled flag
        const unstructuredCollectionsEnabledFlag = client.variation(
          'platform-fe-unstructured-collections',
          true,
        );
        setIsUnstructuredCollectionsEnabled(unstructuredCollectionsEnabledFlag);

        // Get structured collections enabled flag
        const structuredCollectionsEnabledFlag = client.variation(
          'platform-fe-structured-collections',
          true,
        );
        setIsStructuredCollectionsEnabled(structuredCollectionsEnabledFlag);

        // Get collection source enabled flag
        const collectionSourceEnabledFlag = client.variation(
          'platform-fe-collection-source',
          true,
        );
        setIsCollectionSourceEnabled(collectionSourceEnabledFlag);

        // Get documentation enabled flag
        const documentationEnabledFlag = client.variation(
          'platform-fe-documentation',
          true,
        );
        setIsDocumentationEnabled(documentationEnabledFlag);

        // Get API keys enabled flag
        const apiKeysEnabledFlag = client.variation(
          'platform-fe-api-keys',
          true,
        );
        setIsApiKeysEnabled(apiKeysEnabledFlag);

        // Get user personal API tokens (PAT) enabled flag
        const userApiTokensEnabledFlag = client.variation(
          'platform-fe-user-api-tokens',
          false,
        );
        setIsUserApiTokensEnabled(userApiTokensEnabledFlag);

        // Per-entry Documentation sidebar flags
        setIsDocsWorkflowApiEnabled(
          client.variation(
            'platform-fe-docs-workflow-api',
            SAFE_DEFAULTS.isDocsWorkflowApiEnabled,
          ),
        );

        // Get release notes enabled flag
        const releaseNotesEnabledFlag = client.variation(
          'platform-fe-release-notes',
          true,
        );
        setIsReleaseNotesEnabled(releaseNotesEnabledFlag);

        // Get sandbox banner message flag
        const sandboxBannerMessageFlag = client.variation(
          'platform-fe-sandbox-banner-message',
          '',
        );
        setSandboxBannerMessage(sandboxBannerMessageFlag || '');

        // Get collection chat enabled flag
        const collectionChatEnabledFlag = client.variation(
          'platform-fe-collection-chat',
          true,
        );
        setIsCollectionChatEnabled(collectionChatEnabledFlag);

        // Get chat agents enabled flag
        const chatAgentsEnabledFlag = client.variation(
          'platform-fe-chat-agents',
          true,
        );
        setIsChatAgentsEnabled(chatAgentsEnabledFlag);

        const agentsEnabledFlag = client.variation('platform-fe-agents', false);
        setIsAgentsEnabled(agentsEnabledFlag);

        const primaryChatEnabledFlag = client.variation(
          'platform-fe-primary-chat',
          false,
        );
        setIsPrimaryChatEnabled(primaryChatEnabledFlag);

        const chatsPageEnabledFlag = client.variation(
          'platform-fe-chats-page',
          false,
        );
        setIsChatsPageEnabled(chatsPageEnabledFlag);

        const chatSharingEnabledFlag = client.variation(
          'platform-fe-chat-sharing',
          false,
        );
        setIsChatSharingEnabled(chatSharingEnabledFlag);

        // Get composer dashboard enabled flag
        const composerDashboardEnabledFlag = client.variation(
          'platform-fe-composer-dashboard',
          true,
        );
        setIsComposerDashboardEnabled(composerDashboardEnabledFlag);

        // Get home redirect route flag
        const homeRedirectRouteFlag = client.variation(
          'platform-fe-home-redirect-route',
          '',
        );
        setHomeRedirectRoute(homeRedirectRouteFlag || '');

        // Get degraded service banner message flag
        const degradedServiceBannerMessageFlag = client.variation(
          'platform-fe-degraded-service-banner',
          '',
        );
        setDegradedServiceBannerMessage(degradedServiceBannerMessageFlag);

        // Get scheduled maintenance banner message flag
        const scheduledMaintenanceBannerMessageFlag = client.variation(
          'platform-fe-scheduled-maintenance-banner',
          '',
        );
        setScheduledMaintenanceBannerMessage(
          scheduledMaintenanceBannerMessageFlag,
        );

        // Get guardrail active toggle flag
        const guardrailActiveToggleFlag = client.variation(
          'platform-fe-guardrail-active-toggle',
          SAFE_DEFAULTS.isGuardrailActiveToggleEnabled,
        );
        setIsGuardrailActiveToggleEnabled(guardrailActiveToggleFlag);

        // Get org nodes & tools flag
        const orgNodesToolsFlag = client.variation(
          'platform-fe-org-nodes-tools',
          false,
        );
        setIsOrgNodesToolsEnabled(orgNodesToolsFlag);

        // Get org MCP tools flag
        const orgMcpToolsFlag = client.variation(
          'platform-fe-org-mcp-tools',
          false,
        );
        setIsOrgMcpToolsEnabled(orgMcpToolsFlag);

        // Get triggers flag
        const triggersFlag = client.variation('platform-fe-triggers', false);
        setIsTriggersEnabled(triggersFlag);

        // Get feedback enabled flag
        const feedbackEnabledFlag = client.variation(
          'platform-fe-feedback',
          false,
        );
        setIsFeedbackEnabled(feedbackEnabledFlag);

        const labelsEnabledFlag = client.variation('platform-fe-labels', false);
        setIsLabelsEnabled(labelsEnabledFlag);

        const dashboardUserInfoEnabledFlag = client.variation(
          'platform-fe-dashboard-user-info',
          false,
        );
        setIsDashboardUserInfoEnabled(dashboardUserInfoEnabledFlag);

        const dashboardModelDistributionEnabledFlag = client.variation(
          'platform-fe-dashboard-model-distribution',
          false,
        );
        setIsDashboardModelDistributionEnabled(
          dashboardModelDistributionEnabledFlag,
        );

        const dashboardTokenSpendEnabledFlag = client.variation(
          'platform-fe-dashboard-token-spend',
          false,
        );
        setIsDashboardTokenSpendEnabled(dashboardTokenSpendEnabledFlag);

        const dashboardTopWorkflowsCostEnabledFlag = client.variation(
          'platform-fe-dashboard-top-workflows-cost',
          false,
        );
        setIsDashboardTopWorkflowsCostEnabled(
          dashboardTopWorkflowsCostEnabledFlag,
        );

        // Get builder agent owner-only flag
        const builderAgentOwnerOnlyFlag = client.variation(
          'platform-fe-builder-agent-owner-only',
          false,
        );
        setIsBuilderAgentOwnerOnly(builderAgentOwnerOnlyFlag);

        const shareClientDataWarningFlag = client.variation(
          'platform-fe-share-client-data-warning',
          false,
        );
        setIsShareClientDataWarningEnabled(shareClientDataWarningFlag);

        const externalChatEnabledFlag = client.variation(
          'platform-fe-external-chat',
          false,
        );
        setIsExternalChatEnabled(externalChatEnabledFlag);

        const uploadApiEnabledFlag = client.variation(
          'platform-fe-upload-api',
          false,
        );
        setIsUploadApiEnabled(uploadApiEnabledFlag);

        const workflowLockingEnabledFlag = client.variation(
          'platform-fe-workflow-locking',
          false,
        );
        setIsWorkflowLockingEnabled(workflowLockingEnabledFlag);

        const qualificationEnabledFlag = client.variation(
          'platform-fe-qualification',
          false,
        );
        setIsQualificationEnabled(qualificationEnabledFlag);

        const workflowSchedulingEnabledFlag = client.variation(
          'platform-fe-workflow-scheduling',
          false,
        );
        setIsWorkflowSchedulingEnabled(workflowSchedulingEnabledFlag);

        const workflowRunCostEnabledFlag = client.variation(
          'platform-fe-workflow-run-cost',
          false,
        );
        setIsWorkflowRunCostEnabled(workflowRunCostEnabledFlag);

        const scheduleRunCostEnabledFlag = client.variation(
          'platform-fe-schedule-run-cost',
          false,
        );
        setIsScheduleRunCostEnabled(scheduleRunCostEnabledFlag);

        const skillsEnabledFlag = client.variation('platform-fe-skills', false);
        setIsSkillsEnabled(skillsEnabledFlag);

        const artifactsEnabledFlag = client.variation(
          'platform-fe-artifacts',
          false,
        );
        setIsArtifactsEnabled(artifactsEnabledFlag);

        const filesEnabledFlag = client.variation('platform-fe-files', false);
        setIsFilesEnabled(filesEnabledFlag);

        const canvasA2aChatEnabledFlag = client.variation(
          'platform-fe-canvas-a2a-chat',
          false,
        );
        setIsCanvasA2aChatEnabled(canvasA2aChatEnabledFlag);

        const creatorEmailTruncatedFlag = client.variation(
          'platform-fe-creator-email-truncated',
          false,
        );
        setIsCreatorEmailTruncated(creatorEmailTruncatedFlag);

        const notificationCenterEnabledFlag = client.variation(
          'platform-fe-notification-center',
          false,
        );
        setIsNotificationCenterEnabled(notificationCenterEnabledFlag);

        // Off in every environment: the auto_generate_* endpoints were
        // decommissioned, so enabling this restores a button that only errors.
        const evalCriteriaAutogenEnabledFlag = client.variation(
          'platform-fe-eval-criteria-autogen',
          false,
        );
        setIsEvalCriteriaAutogenEnabled(evalCriteriaAutogenEnabledFlag);

        // Off in every environment: create-guardrail-config-from-description
        // still calls the decommissioned cap-llm service.
        const guardrailsConfigAutogenEnabledFlag = client.variation(
          'platform-fe-guardrails-config-autogen',
          false,
        );
        setIsGuardrailsConfigAutogenEnabled(guardrailsConfigAutogenEnabledFlag);

        // Listen for flag changes
        client.on('change:platform-fe-outage', (newValue: string) => {
          setIsMaintenanceMode(!!newValue);
          setMaintenanceMessage(newValue || '');
        });

        client.on('change:platform-fe-evals', (newValue: boolean) => {
          setIsEvalsEnabled(newValue);
        });

        client.on('change:platform-fe-schedule', (newValue: boolean) => {
          setIsScheduleEnabled(newValue);
        });

        client.on('change:platform-fe-workflow', (newValue: boolean) => {
          setIsWorkflowEnabled(newValue);
        });

        client.on('change:platform-fe-txt2sql', (newValue: boolean) => {
          setIsTxt2sqlEnabled(newValue);
        });

        client.on('change:platform-fe-data-sources', (newValue: boolean) => {
          setIsDataSourcesEnabled(newValue);
        });

        client.on('change:platform-fe-context-graphs', (newValue: boolean) => {
          setIsContextGraphsEnabled(newValue);
        });

        client.on('change:platform-fe-sources', (newValue: boolean) => {
          setIsSourcesEnabled(newValue);
        });

        client.on('change:platform-fe-triggers', (newValue: boolean) => {
          setIsTriggersEnabled(newValue);
        });

        client.on(
          'change:platform-fe-unstructured-collections',
          (newValue: boolean) => {
            setIsUnstructuredCollectionsEnabled(newValue);
          },
        );

        client.on(
          'change:platform-fe-structured-collections',
          (newValue: boolean) => {
            setIsStructuredCollectionsEnabled(newValue);
          },
        );

        client.on(
          'change:platform-fe-collection-source',
          (newValue: boolean) => {
            setIsCollectionSourceEnabled(newValue);
          },
        );

        client.on('change:platform-fe-documentation', (newValue: boolean) => {
          setIsDocumentationEnabled(newValue);
        });

        client.on('change:platform-fe-api-keys', (newValue: boolean) => {
          setIsApiKeysEnabled(newValue);
        });

        client.on('change:platform-fe-user-api-tokens', (newValue: boolean) => {
          setIsUserApiTokensEnabled(newValue);
        });

        client.on(
          'change:platform-fe-docs-workflow-api',
          (newValue: boolean) => {
            setIsDocsWorkflowApiEnabled(newValue);
          },
        );

        client.on('change:platform-fe-release-notes', (newValue: boolean) => {
          setIsReleaseNotesEnabled(newValue);
        });

        client.on(
          'change:platform-fe-sandbox-banner-message',
          (newValue: string) => {
            setSandboxBannerMessage(newValue || '');
          },
        );

        client.on('change:platform-fe-collection-chat', (newValue: boolean) => {
          setIsCollectionChatEnabled(newValue);
        });

        client.on('change:platform-fe-chat-agents', (newValue: boolean) => {
          setIsChatAgentsEnabled(newValue);
        });

        client.on('change:platform-fe-agents', (newValue: boolean) => {
          setIsAgentsEnabled(newValue);
        });

        client.on('change:platform-fe-primary-chat', (newValue: boolean) => {
          setIsPrimaryChatEnabled(newValue);
        });

        client.on('change:platform-fe-chats-page', (newValue: boolean) => {
          setIsChatsPageEnabled(newValue);
        });

        client.on('change:platform-fe-chat-sharing', (newValue: boolean) => {
          setIsChatSharingEnabled(newValue);
        });

        client.on(
          'change:platform-fe-composer-dashboard',
          (newValue: boolean) => {
            setIsComposerDashboardEnabled(newValue);
          },
        );

        client.on(
          'change:platform-fe-home-redirect-route',
          (newValue: string) => {
            setHomeRedirectRoute(newValue || '');
          },
        );

        client.on(
          'change:platform-fe-degraded-service-banner',
          (newValue: string) => {
            setDegradedServiceBannerMessage(newValue);
          },
        );

        client.on(
          'change:platform-fe-scheduled-maintenance-banner',
          (newValue: string) => {
            setScheduledMaintenanceBannerMessage(newValue);
          },
        );

        client.on(
          'change:platform-fe-guardrail-active-toggle',
          (newValue: boolean) => {
            setIsGuardrailActiveToggleEnabled(newValue);
          },
        );

        client.on('change:platform-fe-org-nodes-tools', (newValue: boolean) => {
          setIsOrgNodesToolsEnabled(newValue);
        });

        client.on('change:platform-fe-org-mcp-tools', (newValue: boolean) => {
          setIsOrgMcpToolsEnabled(newValue);
        });

        client.on('change:platform-fe-feedback', (newValue: boolean) => {
          setIsFeedbackEnabled(newValue);
        });

        client.on('change:platform-fe-labels', (newValue: boolean) => {
          setIsLabelsEnabled(newValue);
        });

        client.on(
          'change:platform-fe-dashboard-user-info',
          (newValue: boolean) => {
            setIsDashboardUserInfoEnabled(newValue);
          },
        );

        client.on(
          'change:platform-fe-dashboard-model-distribution',
          (newValue: boolean) => {
            setIsDashboardModelDistributionEnabled(newValue);
          },
        );

        client.on(
          'change:platform-fe-dashboard-token-spend',
          (newValue: boolean) => {
            setIsDashboardTokenSpendEnabled(newValue);
          },
        );

        client.on(
          'change:platform-fe-dashboard-top-workflows-cost',
          (newValue: boolean) => {
            setIsDashboardTopWorkflowsCostEnabled(newValue);
          },
        );

        client.on(
          'change:platform-fe-builder-agent-owner-only',
          (newValue: boolean) => {
            setIsBuilderAgentOwnerOnly(newValue);
          },
        );

        client.on(
          'change:platform-fe-share-client-data-warning',
          (newValue: boolean) => {
            setIsShareClientDataWarningEnabled(newValue);
          },
        );

        client.on('change:platform-fe-external-chat', (newValue: boolean) => {
          setIsExternalChatEnabled(newValue);
        });

        client.on('change:platform-fe-upload-api', (newValue: boolean) => {
          setIsUploadApiEnabled(newValue);
        });

        client.on(
          'change:platform-fe-workflow-locking',
          (newValue: boolean) => {
            setIsWorkflowLockingEnabled(newValue);
          },
        );

        client.on('change:platform-fe-qualification', (newValue: boolean) => {
          setIsQualificationEnabled(newValue);
        });

        client.on(
          'change:platform-fe-workflow-scheduling',
          (newValue: boolean) => {
            setIsWorkflowSchedulingEnabled(newValue);
          },
        );

        client.on(
          'change:platform-fe-workflow-run-cost',
          (newValue: boolean) => {
            setIsWorkflowRunCostEnabled(newValue);
          },
        );

        client.on(
          'change:platform-fe-schedule-run-cost',
          (newValue: boolean) => {
            setIsScheduleRunCostEnabled(newValue);
          },
        );

        client.on('change:platform-fe-skills', (newValue: boolean) => {
          setIsSkillsEnabled(newValue);
        });

        client.on('change:platform-fe-artifacts', (newValue: boolean) => {
          setIsArtifactsEnabled(newValue);
        });

        client.on('change:platform-fe-files', (newValue: boolean) => {
          setIsFilesEnabled(newValue);
        });

        client.on('change:platform-fe-canvas-a2a-chat', (newValue: boolean) => {
          setIsCanvasA2aChatEnabled(newValue);
        });

        client.on(
          'change:platform-fe-creator-email-truncated',
          (newValue: boolean) => {
            setIsCreatorEmailTruncated(newValue);
          },
        );

        client.on(
          'change:platform-fe-notification-center',
          (newValue: boolean) => {
            setIsNotificationCenterEnabled(newValue);
          },
        );

        client.on(
          'change:platform-fe-eval-criteria-autogen',
          (newValue: boolean) => {
            setIsEvalCriteriaAutogenEnabled(newValue);
          },
        );

        client.on(
          'change:platform-fe-guardrails-config-autogen',
          (newValue: boolean) => {
            setIsGuardrailsConfigAutogenEnabled(newValue);
          },
        );

        // Set loading to false after all flags are initialized
        setIsLoading(false);
      } catch (error) {
        console.warn('LaunchDarkly initialization failed:', error);
        // Safe fallback: reset to conservative defaults
        setIsMaintenanceMode(SAFE_DEFAULTS.isMaintenanceMode);
        setMaintenanceMessage(SAFE_DEFAULTS.maintenanceMessage);
        setIsEvalsEnabled(SAFE_DEFAULTS.isEvalsEnabled);
        setIsScheduleEnabled(SAFE_DEFAULTS.isScheduleEnabled);
        setIsWorkflowEnabled(SAFE_DEFAULTS.isWorkflowEnabled);
        setIsTxt2sqlEnabled(SAFE_DEFAULTS.isTxt2sqlEnabled);
        setIsDataSourcesEnabled(SAFE_DEFAULTS.isDataSourcesEnabled);
        setIsContextGraphsEnabled(SAFE_DEFAULTS.isContextGraphsEnabled);
        setIsSourcesEnabled(SAFE_DEFAULTS.isSourcesEnabled);
        setIsUnstructuredCollectionsEnabled(
          SAFE_DEFAULTS.isUnstructuredCollectionsEnabled,
        );
        setIsStructuredCollectionsEnabled(
          SAFE_DEFAULTS.isStructuredCollectionsEnabled,
        );
        setIsCollectionSourceEnabled(SAFE_DEFAULTS.isCollectionSourceEnabled);
        setIsDocumentationEnabled(SAFE_DEFAULTS.isDocumentationEnabled);
        setIsApiKeysEnabled(SAFE_DEFAULTS.isApiKeysEnabled);
        setIsUserApiTokensEnabled(SAFE_DEFAULTS.isUserApiTokensEnabled);
        setIsDocsWorkflowApiEnabled(SAFE_DEFAULTS.isDocsWorkflowApiEnabled);
        setIsReleaseNotesEnabled(SAFE_DEFAULTS.isReleaseNotesEnabled);
        setSandboxBannerMessage(SAFE_DEFAULTS.sandboxBannerMessage);
        setIsCollectionChatEnabled(SAFE_DEFAULTS.isCollectionChatEnabled);
        setIsAgentsEnabled(SAFE_DEFAULTS.isAgentsEnabled);
        setIsChatAgentsEnabled(SAFE_DEFAULTS.isChatAgentsEnabled);
        setIsPrimaryChatEnabled(SAFE_DEFAULTS.isPrimaryChatEnabled);
        setIsChatsPageEnabled(SAFE_DEFAULTS.isChatsPageEnabled);
        setIsChatSharingEnabled(SAFE_DEFAULTS.isChatSharingEnabled);
        setIsComposerDashboardEnabled(SAFE_DEFAULTS.isComposerDashboardEnabled);
        setHomeRedirectRoute(SAFE_DEFAULTS.homeRedirectRoute);
        setDegradedServiceBannerMessage(
          SAFE_DEFAULTS.degradedServiceBannerMessage,
        );
        setScheduledMaintenanceBannerMessage(
          SAFE_DEFAULTS.scheduledMaintenanceBannerMessage,
        );
        setIsGuardrailActiveToggleEnabled(
          SAFE_DEFAULTS.isGuardrailActiveToggleEnabled,
        );
        setIsOrgNodesToolsEnabled(SAFE_DEFAULTS.isOrgNodesToolsEnabled);
        setIsOrgMcpToolsEnabled(SAFE_DEFAULTS.isOrgMcpToolsEnabled);
        setIsTriggersEnabled(SAFE_DEFAULTS.isTriggersEnabled);
        setIsFeedbackEnabled(SAFE_DEFAULTS.isFeedbackEnabled);
        setIsLabelsEnabled(SAFE_DEFAULTS.isLabelsEnabled);
        setIsDashboardUserInfoEnabled(SAFE_DEFAULTS.isDashboardUserInfoEnabled);
        setIsDashboardModelDistributionEnabled(
          SAFE_DEFAULTS.isDashboardModelDistributionEnabled,
        );
        setIsDashboardTokenSpendEnabled(
          SAFE_DEFAULTS.isDashboardTokenSpendEnabled,
        );
        setIsDashboardTopWorkflowsCostEnabled(
          SAFE_DEFAULTS.isDashboardTopWorkflowsCostEnabled,
        );
        setIsBuilderAgentOwnerOnly(SAFE_DEFAULTS.isBuilderAgentOwnerOnly);
        setIsShareClientDataWarningEnabled(
          SAFE_DEFAULTS.isShareClientDataWarningEnabled,
        );
        setIsExternalChatEnabled(SAFE_DEFAULTS.isExternalChatEnabled);
        setIsUploadApiEnabled(SAFE_DEFAULTS.isUploadApiEnabled);
        setIsWorkflowLockingEnabled(SAFE_DEFAULTS.isWorkflowLockingEnabled);
        setIsQualificationEnabled(SAFE_DEFAULTS.isQualificationEnabled);
        setIsWorkflowSchedulingEnabled(
          SAFE_DEFAULTS.isWorkflowSchedulingEnabled,
        );
        setIsWorkflowRunCostEnabled(SAFE_DEFAULTS.isWorkflowRunCostEnabled);
        setIsScheduleRunCostEnabled(SAFE_DEFAULTS.isScheduleRunCostEnabled);
        setIsSkillsEnabled(SAFE_DEFAULTS.isSkillsEnabled);
        setIsArtifactsEnabled(SAFE_DEFAULTS.isArtifactsEnabled);
        setIsFilesEnabled(SAFE_DEFAULTS.isFilesEnabled);
        setIsCanvasA2aChatEnabled(SAFE_DEFAULTS.isCanvasA2aChatEnabled);
        setIsCreatorEmailTruncated(SAFE_DEFAULTS.isCreatorEmailTruncated);
        setIsNotificationCenterEnabled(
          SAFE_DEFAULTS.isNotificationCenterEnabled,
        );
        setIsEvalCriteriaAutogenEnabled(
          SAFE_DEFAULTS.isEvalCriteriaAutogenEnabled,
        );
        setIsGuardrailsConfigAutogenEnabled(
          SAFE_DEFAULTS.isGuardrailsConfigAutogenEnabled,
        );
        setIsLoading(false);
      }
    };

    initLaunchDarkly();
  }, [orgId]);

  const value: LaunchDarklyContextType = {
    isMaintenanceMode,
    maintenanceMessage,
    isEvalsEnabled,
    isScheduleEnabled,
    isWorkflowEnabled,
    isTxt2sqlEnabled,
    isDataSourcesEnabled,
    isContextGraphsEnabled,
    isSourcesEnabled,
    isUnstructuredCollectionsEnabled,
    isStructuredCollectionsEnabled,
    isCollectionSourceEnabled,
    isDocumentationEnabled,
    isApiKeysEnabled,
    isUserApiTokensEnabled,
    isDocsWorkflowApiEnabled,
    isReleaseNotesEnabled,
    sandboxBannerMessage,
    isCollectionChatEnabled,
    isChatAgentsEnabled,
    isAgentsEnabled,
    isPrimaryChatEnabled,
    isChatsPageEnabled,
    isChatSharingEnabled,
    isComposerDashboardEnabled,
    homeRedirectRoute,
    degradedServiceBannerMessage,
    scheduledMaintenanceBannerMessage,
    isGuardrailActiveToggleEnabled,
    isOrgNodesToolsEnabled,
    isOrgMcpToolsEnabled,
    isTriggersEnabled,
    isFeedbackEnabled,
    isLabelsEnabled,
    isDashboardUserInfoEnabled,
    isDashboardModelDistributionEnabled,
    isDashboardTokenSpendEnabled,
    isDashboardTopWorkflowsCostEnabled,
    isBuilderAgentOwnerOnly,
    isShareClientDataWarningEnabled,
    isExternalChatEnabled,
    isUploadApiEnabled,
    isWorkflowLockingEnabled,
    isQualificationEnabled,
    isWorkflowSchedulingEnabled,
    isWorkflowRunCostEnabled,
    isScheduleRunCostEnabled,
    isSkillsEnabled,
    isArtifactsEnabled,
    isFilesEnabled,
    isCanvasA2aChatEnabled,
    isCreatorEmailTruncated,
    isNotificationCenterEnabled,
    isEvalCriteriaAutogenEnabled,
    isGuardrailsConfigAutogenEnabled,
    ldClient,
    isLoading,
  };

  return (
    <LaunchDarklyContext.Provider value={value}>
      {children}
    </LaunchDarklyContext.Provider>
  );
}
